/**
 * Retailer Capabilities Configuration
 * 
 * Defines what data and features each retailer supports.
 * Used to dynamically show/hide navigation items based on selected retailer.
 * 
 * When adding new retailers, map their ID to capabilities here.
 */

// ============================================================================
// TYPES
// ============================================================================

export interface RetailerCapabilities {
  // Data availability
  hasKpiSummary: boolean;          // curated_table_summary_newformat table exists
  hasStoreBreakdown: boolean;      // store vs online channel split
  hasYearOverYear: boolean;        // Last year comparison data
  hasMarginData: boolean;          // Cost and margin fields
  hasInventoryDetail: boolean;     // EOH, WOS metrics
  hasDistributionMetrics: boolean; // OOS%, distribution% fields
  hasPricingDetail: boolean;       // Retail price, realization metrics
  hasStoreGeography: boolean;      // Store-level geographic/demographic data
  
  // ML/AI capabilities (require sufficient historical data)
  hasForecastingData: boolean;     // 2+ years of historical data for ML
  hasStoreClustering: boolean;     // Store clustering analysis available
  hasPlanogramData: boolean;       // Planogram/shelf layout data
  
  // Supported page slugs (pages that work for this retailer)
  supportedPages: string[];
}

// ============================================================================
// KNOWN RETAILER IDS
// ============================================================================

export const RETAILER_IDS = {
  ULTA: '10000000-0000-0000-0000-000000000001',
  JCP: '10000000-0000-0000-0000-000000000004',
  MACYS: '10000000-0000-0000-0000-000000000003',
  // Add more retailer IDs as they're onboarded
} as const;

// ============================================================================
// RETAILER CAPABILITIES MAPPING
// ============================================================================

export const RETAILER_CAPABILITIES: Record<string, RetailerCapabilities> = {
  // Ulta Beauty - Full data availability (gold standard)
  [RETAILER_IDS.ULTA]: {
    hasKpiSummary: true,
    hasStoreBreakdown: true,
    hasYearOverYear: true,
    hasMarginData: true,
    hasInventoryDetail: true,
    hasDistributionMetrics: true,
    hasPricingDetail: true,
    hasStoreGeography: false,
    hasForecastingData: true,
    hasStoreClustering: true,
    hasPlanogramData: true,
    supportedPages: [
      // Core Analytics
      '/dashboard',
      '/pricing-margin',
      '/distribution-availability',
      '/concentration-risk',
      '/retail-support',
      
      // Deep Dive Analysis
      '/products',
      '/stores',
      '/inventory',
      '/revenue-detail',
      '/units-detail',
      '/skus-detail',
      '/store-performance-deep-dive',
      
      // PatternIQ AI
      '/command-center',  // Phase E Command Center
      '/agent-hub',
      '/agents/social-listening',  // Phase F Social Listening
      '/advocates/discovery',
      '/advocates/profile',
      '/advocates/offers',
      '/advocates/performance',
      '/agents/competitor-intel',  // Phase F Competitor Intel
      '/agents/creative-intel',    // Phase F Creative Intel
      '/agents/retail-story',      // Phase F Retail Story
      '/agents/npd-opportunity',   // Phase F NPD Opportunity
      '/agents/merch-optimization', // Phase F Merch Optimization
      '/agents/marketing-mix',     // Phase F Marketing Mix
      '/agents/pdp-optimization',  // Phase F PDP Optimization
      '/agents/supply-chain',      // Phase F Supply Chain
      '/agents/cx-intel',          // Phase F CX Intelligence
      '/ai/sales-forecaster',
      '/ai/forecast-anomalies',
      '/ai/inventory-intelligence',
      '/ai/stockout-early-warning',
      '/ai/retail-support-optimizer',
      '/ai/store-clusters',
      '/agents/merch-optimization', // Planogram merged here
      '/ai/gpt',
      
      // PIQ Machine Learning
      '/ml/dashboard',
      '/ml/sku-demand',
      '/ml/stockout',
      '/ml/models',
    ],
  },
  
  // JCPenney - Limited data (38 weeks only, no summary table)
  [RETAILER_IDS.JCP]: {
    hasKpiSummary: false,          // No summary table
    hasStoreBreakdown: false,      // No channel split
    hasYearOverYear: false,        // Only 38 weeks of data
    hasMarginData: true,           // Has purch_cost_average, retail_price_current
    hasInventoryDetail: true,      // Has EOH units
    hasDistributionMetrics: true,  // Has weekly_total_available_st_pct
    hasPricingDetail: true,        // Has retail_price_current
    hasStoreGeography: true,       // Has region, climate, fashion_segment
    hasForecastingData: false,     // Insufficient historical data (< 1 year)
    hasStoreClustering: true,      // Has geographic data for clustering
    hasPlanogramData: false,       // No planogram data
    supportedPages: [
      // Core Analytics
      '/dashboard',
      '/pricing-margin',
      '/distribution-availability',
      '/concentration-risk',
      '/retail-support',
      
      // Deep Dive Analysis
      '/products',
      '/stores',
      '/inventory',
      '/revenue-detail',
      '/units-detail',
      '/skus-detail',
      '/store-performance-deep-dive',
      
      // PatternIQ AI (NOW INCLUDES REAL ML FORECASTING!)
      '/command-center',  // Phase E Command Center
      '/agent-hub',
      '/agents/social-listening',  // Phase F Social Listening
      '/advocates/discovery',
      '/advocates/profile',
      '/advocates/offers',
      '/advocates/performance',
      '/agents/competitor-intel',  // Phase F Competitor Intel
      '/agents/creative-intel',    // Phase F Creative Intel
      '/agents/retail-story',      // Phase F Retail Story
      '/agents/npd-opportunity',   // Phase F NPD Opportunity
      '/agents/merch-optimization', // Phase F Merch Optimization
      '/agents/marketing-mix',     // Phase F Marketing Mix
      '/agents/pdp-optimization',  // Phase F PDP Optimization
      '/agents/supply-chain',      // Phase F Supply Chain
      '/agents/cx-intel',          // Phase F CX Intelligence
      '/ai/sales-forecaster',        // ✅ JCP model trained (5.05% MAPE, 27 forecasts)
      '/ai/forecast-anomalies',      // ✅ Forecast Lab working for JCP
      '/ai/inventory-intelligence',
      '/ai/stockout-early-warning',
      '/ai/store-clusters',
      '/ai/gpt',
      // EXCLUDED: /ai/retail-support-optimizer (no data yet)
      
      // PIQ Machine Learning
      '/ml/dashboard',
      '/ml/sku-demand',
      '/ml/stockout',
      '/ml/models',
    ],
  },
  
  // Macys - Calendar-based dates with P9W4 period labels (NOW WITH HISTORIC STORE DATA!)
  [RETAILER_IDS.MACYS]: {
    hasKpiSummary: false,          // No summary table (build from SKU data)
    hasStoreBreakdown: true,       // Has STR vs COM split in SKU data
    hasYearOverYear: true,          // Has LY comparison data
    hasMarginData: false,          // NO COST DATA AVAILABLE - cannot calculate margins
    hasInventoryDetail: true,      // Has OH (on hand) and OO (on order) metrics
    hasDistributionMetrics: true,  // Has sell-through (S/O U) metrics
    hasPricingDetail: true,        // Has Site Price, AUR metrics
    hasStoreGeography: true,       // ✅ NOW HAS: Zone (COLD/HOT/MILD) and SubZone data (70 weeks, 574 stores)
    hasForecastingData: true,      // ✅ NOW HAS: 70 weeks of historical data (Feb 2024 - Oct 2025)
    hasStoreClustering: true,      // ✅ NOW HAS: Geographic data (Zone/SubZone) for clustering
    hasPlanogramData: false,       // No planogram data
    supportedPages: [
      // Core Analytics
      '/dashboard',
      '/pricing-margin',         // PRICING ONLY - no margin calcs (no cost data)
      '/distribution-availability',
      '/concentration-risk',
      '/retail-support',         // ✅ NOW AVAILABLE with store data
      
      // Deep Dive Analysis - ✅ ALL STORE PAGES NOW AVAILABLE
      '/products',
      '/stores',                       // ✅ NOW AVAILABLE (70 weeks, 574 stores)
      '/inventory',
      '/revenue-detail',
      '/units-detail',
      '/skus-detail',
      '/store-performance-deep-dive',  // ✅ NOW AVAILABLE
      
      // PatternIQ AI
      '/command-center',  // Phase E Command Center
      '/agent-hub',
      '/agents/social-listening',  // Phase F Social Listening
      '/advocates/discovery',
      '/advocates/profile',
      '/advocates/offers',
      '/advocates/performance',
      '/agents/competitor-intel',  // Phase F Competitor Intel
      '/agents/creative-intel',    // Phase F Creative Intel
      '/agents/retail-story',      // Phase F Retail Story
      '/agents/npd-opportunity',   // Phase F NPD Opportunity
      '/agents/merch-optimization', // Phase F Merch Optimization
      '/agents/marketing-mix',     // Phase F Marketing Mix
      '/agents/pdp-optimization',  // Phase F PDP Optimization
      '/agents/supply-chain',      // Phase F Supply Chain
      '/agents/cx-intel',          // Phase F CX Intelligence
      '/ai/sales-forecaster',        // ✅ Macy's model trained (5.61% MAPE, 74 forecasts - WORLD-CLASS!)
      '/ai/forecast-anomalies',      // ✅ Forecast Lab working for Macy's
      '/ai/inventory-intelligence',  // ✅ NOW AVAILABLE with 70 weeks data
      '/ai/stockout-early-warning',
      '/ai/retail-support-optimizer',// ✅ NOW AVAILABLE with store data
      '/ai/store-clusters',          // ✅ NOW AVAILABLE (Zone/SubZone clustering)
      '/agents/merch-optimization', // Planogram Labs merged here
      '/ai/gpt',
      
      // PIQ Machine Learning
      '/ml/dashboard',
      '/ml/sku-demand',
      '/ml/stockout',
      '/ml/models',
    ],
  },
};

// ============================================================================
// DEFAULT CAPABILITIES (for unknown retailers)
// ============================================================================

const DEFAULT_CAPABILITIES: RetailerCapabilities = {
  hasKpiSummary: false,
  hasStoreBreakdown: false,
  hasYearOverYear: false,
  hasMarginData: false,
  hasInventoryDetail: false,
  hasDistributionMetrics: false,
  hasPricingDetail: false,
  hasStoreGeography: false,
  hasForecastingData: false,
  hasStoreClustering: false,
  hasPlanogramData: false,
  supportedPages: [
    // Only basic pages for unknown retailers
    '/dashboard',
    '/products',
    '/stores',
    '/inventory',
  ],
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get capabilities for a specific retailer
 * @param retailerId - UUID of the retailer
 * @returns RetailerCapabilities object
 */
export function getRetailerCapabilities(retailerId: string | null): RetailerCapabilities {
  if (!retailerId) {
    // When "Overview (All Retailers)" is selected, return capabilities for aggregate view
    // EXCLUDE retailer-specific AI/ML features that don't make sense in aggregate
    return {
      hasKpiSummary: true,
      hasStoreBreakdown: true,
      hasYearOverYear: true,
      hasMarginData: true,
      hasInventoryDetail: true,
      hasDistributionMetrics: true,
      hasPricingDetail: true,
      hasStoreGeography: true,
      hasForecastingData: false,  // No forecasting in overview mode
      hasStoreClustering: false,   // No clustering in overview mode
      hasPlanogramData: false,     // No planogram in overview mode
      supportedPages: [
        // Core Analytics (aggregate across all retailers)
        '/dashboard',
        '/pricing-margin',
        '/distribution-availability',
        '/concentration-risk',
        '/retail-support',
        
        // Deep Dive Analysis
        '/products',
        '/stores',
        '/inventory',
        '/revenue-detail',
        '/units-detail',
        '/skus-detail',
        '/store-performance-deep-dive',
        
        // PIQ Machine Learning - ALWAYS show ML navigation (user can select retailer from header)
        '/ml/dashboard',
        '/ml/sku-demand',
        '/ml/stockout',
        '/ml/models',
        
        // PatternIQ AI pages - show in overview, user selects retailer from header
        '/command-center',  // Phase E Command Center
        '/agent-hub',
        '/agents/social-listening',  // Phase F Social Listening
        '/agents/competitor-intel',  // Phase F Competitor Intel
        '/agents/creative-intel',    // Phase F Creative Intel
        '/agents/retail-story',      // Phase F Retail Story
        '/agents/npd-opportunity',   // Phase F NPD Opportunity
        '/ai/sales-forecaster',
        '/ai/forecast-anomalies',
        '/ai/inventory-intelligence',
        ],
    };
  }
  
  return RETAILER_CAPABILITIES[retailerId] || DEFAULT_CAPABILITIES;
}

/**
 * Check if a page is supported for a given retailer
 * @param retailerId - UUID of the retailer (null = all retailers)
 * @param pageSlug - Page path (e.g., '/pricing-margin')
 * @returns true if page should be visible
 */
export function isPageSupportedForRetailer(
  retailerId: string | null,
  pageSlug: string
): boolean {
  // Admin pages are ALWAYS available regardless of retailer selection
  if (pageSlug.startsWith('/admin/')) {
    return true;
  }

  // Agent Hub is always available (it's the main entry point)
  if (pageSlug === '/agent-hub') {
    return true;
  }

  // Company-wide pages available for all retailers
  if (['/intelligence', '/retail', '/retail-performance'].includes(pageSlug)) {
    return true;
  }
  
  const capabilities = getRetailerCapabilities(retailerId);
  return capabilities.supportedPages.includes(pageSlug);
}

/**
 * Get all pages that should be hidden for a given retailer
 * @param retailerId - UUID of the retailer
 * @returns Array of page slugs that should be hidden
 */
export function getHiddenPagesForRetailer(retailerId: string | null): string[] {
  if (!retailerId) {
    return []; // Show all pages when "All Retailers" selected
  }
  
  const capabilities = getRetailerCapabilities(retailerId);
  const allPages = RETAILER_CAPABILITIES[RETAILER_IDS.ULTA].supportedPages; // Use Ulta as reference
  
  return allPages.filter(page => !capabilities.supportedPages.includes(page));
}

/**
 * Check if retailer has specific capability
 * @param retailerId - UUID of the retailer
 * @param capability - Capability name
 * @returns true if retailer has this capability
 */
export function hasCapability(
  retailerId: string | null,
  capability: keyof Omit<RetailerCapabilities, 'supportedPages'>
): boolean {
  const capabilities = getRetailerCapabilities(retailerId);
  return capabilities[capability];
}

